import './car-info-modal.scss';

import { Modal } from 'antd';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { Autocomplete } from '@/components/Shared/ui';
import { DictTypes } from '@/constants';
import { safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import useCarInfo, {
  type CarInfoInterface,
  type KeyToUse
} from './hooks/useCarInfo';

export type CarModalType =
  | 'autoCredit'
  | 'autoCreditLeasing'
  | 'leasing'
  | 'insurance';
interface IProps {
  isOpened: boolean;
  onClose: () => void;
  onSave: (values: SubmitCarInfoProps) => void;
  keyToUse: KeyToUse;
  defaultValues?: CarInfoInterface;
  type: CarModalType;
}

const CarInfoModal: React.FC<IProps> = ({
  isOpened,
  onClose,
  onSave,
  keyToUse,
  defaultValues,
  type
}) => {
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { dicts } = useAppSelector((state) => state.dict);
  const { dealer: userDealer } = useAppSelector((state) => state.userInfo);

  const globalDealer =
    dicts?.[DictTypes.Dealers]?.getBy.code[
      safeKey(orderApp?.productInfo.product.dealer ?? userDealer?.code)
    ];

  const {
    control,
    formState: { errors },
    setValue,
    reset,
    handleSubmit,
    clearErrors
  } = useForm<CarInfoInterface>({ mode: 'all', delayError: 200 });

  const {
    models,
    possibleModels,
    possibleMarks,
    possibleEquipments,
    possibleDealers,
    years,
    handleMarkChanged,
    handleModelChanged,
    handleDealerChanged
  } = useCarInfo({
    keyToUse,
    defaultValues,
    type,
    forceSetDealer: (dealer: string) => {
      setValue('dealer', dealer);
      clearErrors('dealer');
    }
  });

  const isAutoCreditLeasing = type === 'autoCreditLeasing';

  const [selectedMarkLabel, selectedDealerLabel] = useWatch({
    control,
    name: ['mark', 'dealer']
  });

  useEffect(() => {
    if (defaultValues) {
      reset({
        ...defaultValues,
        year: defaultValues.year || new Date().getFullYear().toString()
      });
    }
  }, [defaultValues]);

  const onSubmit = (values: CarInfoInterface) => {
    const model = possibleModels.find((item) => values.model === item.label);

    onSave({
      mark: {
        [keyToUse]:
          possibleMarks.find((item) => values.mark === item.label)?.value || '',
        name: values.mark
      },
      model: {
        [keyToUse]: model?.value || '',
        name: values.model,
        isCommercial: !!model?.isCommercial
      },
      equipment: {
        uuid:
          possibleEquipments.find((item) => values.equipment === item.label)
            ?.value || '',
        name: values.equipment || ''
      },
      year: values.year,
      dealer: values.dealer
        ? {
            [keyToUse]:
              possibleDealers.find((item) => values.dealer === item.label)
                ?.value || '',
            name: values.dealer
          }
        : undefined
    });

    reset({
      mark: '',
      model: '',
      equipment: '',
      year: ''
    });
  };

  const handleClose = () => {
    onClose();
    reset({
      mark: '',
      model: '',
      equipment: '',
      year: ''
    });
  };

  return (
    <Modal
      mask={{ closable: true }}
      wrapClassName="car-info-modal-wrapper"
      className="car-info-modal"
      classNames={{
        header: 'car-info-modal__header'
      }}
      title="Марка, модель и год выпуска"
      open={isOpened}
      onCancel={handleClose}
      footer={null}
    >
      <form
        className="car-info-modal__form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <Controller
          control={control}
          name="mark"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' },
            validate: (value) => {
              const mark = possibleMarks.find((item) => item.label === value);
              if (!mark) return 'Пожалуйста, выберите существующую марку';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              value={field.value ?? ''}
              label="Марка авто"
              onChange={(value) => {
                field.onChange(value);
                const mark = possibleMarks.find((item) => item.label === value);
                if (mark) {
                  handleMarkChanged(mark.value);
                  setValue('model', '');
                  setValue('equipment', '');
                }
              }}
              options={possibleMarks}
              error={error?.message}
              autoComplete="off"
              isRequired
            />
          )}
        />
        {globalDealer && globalDealer._dealerUuids.length > 0 && (
          <Controller
            control={control}
            name="dealer"
            rules={{
              required: 'Пожалуйста, введите данные',
              minLength: { value: 1, message: 'Пожалуйста, введите данные' },
              validate: (value) => {
                const dealer = possibleDealers.find(
                  (item) => item.label === value
                );
                if (!dealer)
                  return 'Пожалуйста, выберите существующую торговую точку';
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                value={field.value ?? ''}
                label="Торговая точка"
                options={errors?.mark ? [] : possibleDealers}
                error={error?.message}
                autoComplete="off"
                onChange={(value) => {
                  field.onChange(value);
                  const dealer = possibleDealers.find(
                    (item) => item.label === value
                  );
                  if (isAutoCreditLeasing && dealer) {
                    handleDealerChanged(selectedMarkLabel, dealer.value);
                    setValue('model', '');
                    setValue('equipment', '');
                  }
                }}
                isRequired
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="model"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' },
            validate: (value) => {
              if (possibleModels.length === 0) {
                return 'По выбранной марке отсутствуют модели';
              }
              const model = possibleModels.find((item) => item.label === value);
              if (!model) return 'Пожалуйста, выберите существующую модель';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              <Autocomplete
                {...field}
                value={field.value ?? ''}
                label="Модель авто"
                options={errors?.mark ? [] : models}
                error={error?.message}
                autoComplete="off"
                onChange={(value) => {
                  field.onChange(value);
                  const model = possibleModels.find(
                    (item) => item.label === value
                  );
                  if (model) {
                    handleModelChanged(
                      selectedMarkLabel,
                      selectedDealerLabel ?? '',
                      model.value
                    );
                    setValue('equipment', '');
                  }
                }}
                isRequired
              />
            </>
          )}
        />

        {(type === 'autoCredit' || type === 'autoCreditLeasing') && (
          <Controller
            control={control}
            name="equipment"
            rules={{
              required: 'Пожалуйста, введите данные',
              minLength: { value: 1, message: 'Пожалуйста, введите данные' },
              validate: (value) => {
                if (possibleEquipments.length === 0) {
                  return 'По выбранной модели отсутствуют комплектации';
                }
                const equipment = possibleEquipments.find(
                  (item) => item.label === value
                );
                if (!equipment)
                  return 'Пожалуйста, выберите существующую комплектацию';
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                value={field.value ?? ''}
                label="Комплектация авто"
                options={errors?.model ? [] : possibleEquipments}
                error={error?.message}
                autoComplete="off"
                isRequired
              />
            )}
          />
        )}
        <Controller
          control={control}
          name="year"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: {
              value: 4,
              message: 'Год должен содержать 4 цифры'
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              type="tel"
              value={field.value ?? ''}
              label="Год выпуска"
              onChange={(value) => {
                if (value.match(/\D/) || value.length > 4) return;
                field.onChange(value);
              }}
              options={years}
              error={error?.message}
              autoComplete="off"
              isRequired
            />
          )}
        />

        <div className="car-info-modal__actions">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleClose();
            }}
            className="car-info-modal__button car-info-modal__cancel"
          >
            Отмена
          </button>
          <button
            type="submit"
            className="car-info-modal__button car-info-modal__save"
            disabled={Object.values(errors).length !== 0}
          >
            Сохранить
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CarInfoModal;

interface SubmitCarInfoProps {
  mark: {
    uuid?: string;
    code?: string;
    name: string;
  };
  model: {
    uuid?: string;
    code?: string;
    name: string;
    isCommercial: boolean;
  };
  equipment: {
    uuid?: string;
    code?: string;
    name: string;
  };
  year: string;
  dealer?: {
    uuid?: string;
    code?: string;
    name: string;
  };
}
